import { apiService } from './api';
import { PaginationParams } from '../types/common';
import {
  Ingredient,
  IngredientFamily,
  MeasureUnit,
  CreateIngredientRequest,
  UpdateIngredientRequest,
  CreateIngredientFamilyRequest,
  UpdateIngredientFamilyRequest,
  IngredientsResponse,
  IngredientFamiliesResponse,
} from '../types/ingredient';

class IngredientService {
  // Ingredients
  async getIngredients(params?: PaginationParams & { name?: string; status?: string; ingredientFamilyId?: string }): Promise<IngredientsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.ingredientFamilyId) queryParams.append('ingredientFamilyId', params.ingredientFamilyId);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const res = await apiService.get<IngredientsResponse>(`/ingredients?${queryParams.toString()}`);
    return res;
  }

  async getIngredient(id: string): Promise<{ ingredient: Ingredient }> {
    const res = await apiService.get<{ ingredient: Ingredient }>(`/ingredients/${id}`);
    return res;
  }

  async createIngredient(data: CreateIngredientRequest): Promise<{ message: string; ingredient: Ingredient }> {
    const payload: any = {
      code: data.code,
      description: data.description,
      ingredientFamilyId: data.ingredientFamilyId,
      status: data.status || 'ACTIVE',
      unitPrice: data.unitPrice || 0,
      calories: data.calories || 0,
      protein: data.protein || 0,
      carbs: data.carbs || 0,
      fat: data.fat || 0,
      fiber: data.fiber || 0,
      sugar: data.sugar || 0,
      sodium: data.sodium || 0,
    };
    const res = await apiService.post<{ message: string; ingredient: Ingredient }>('/ingredients', payload);
    return res;
  }

  async updateIngredient(id: string, data: UpdateIngredientRequest): Promise<{ message: string; ingredient: Ingredient }> {
    const res = await apiService.put<{ message: string; ingredient: Ingredient }>(`/ingredients/${id}`, data);
    return res;
  }

  async bulkUpdateIngredientStatus(ids: string[], status: 'ACTIVE' | 'INACTIVE'): Promise<{ success: boolean; message: string; count: number }> {
    return apiService.post<{ success: boolean; message: string; count: number }>(`/ingredients/bulk-status`, { ids, status });
  }

  async deleteIngredient(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/ingredients/${id}`);
  }

  // Ingredient Families
  async getIngredientFamilies(params?: PaginationParams & { status?: 'ACTIVE' | 'INACTIVE' }): Promise<IngredientFamiliesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    return apiService.get<IngredientFamiliesResponse>(`/ingredient-families?${queryParams.toString()}`);
  }

  async getIngredientFamiliesAll(): Promise<{ ingredientFamilies: Pick<IngredientFamily, 'id' | 'name'>[] }> {
    return apiService.get<{ ingredientFamilies: Pick<IngredientFamily, 'id' | 'name'>[] }>(`/ingredient-families/all`);
  }

  async getIngredientFamily(id: string): Promise<{ ingredientFamily: IngredientFamily }> {
    return apiService.get<{ ingredientFamily: IngredientFamily }>(`/ingredient-families/${id}`);
  }

  async createIngredientFamily(data: CreateIngredientFamilyRequest): Promise<{ message: string; ingredientFamily: IngredientFamily }> {
    return apiService.post<{ message: string; ingredientFamily: IngredientFamily }>('/ingredient-families', data);
  }

  async updateIngredientFamily(id: string, data: UpdateIngredientFamilyRequest): Promise<{ message: string; ingredientFamily: IngredientFamily }> {
    return apiService.put<{ message: string; ingredientFamily: IngredientFamily }>(`/ingredient-families/${id}`, data);
  }

  async deleteIngredientFamily(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/ingredient-families/${id}`);
  }

  async bulkUpdateIngredientFamilyStatus(ids: string[], status: 'ACTIVE' | 'INACTIVE'): Promise<{ success: boolean; message: string; count: number }> {
    return apiService.post<{ success: boolean; message: string; count: number }>(`/ingredient-families/bulk-status`, { ids, status });
  }

  async mergeIngredientFamily(id: string, targetFamilyId: string): Promise<{ message: string; reassignedCount: number }> {
    return apiService.post<{ message: string; reassignedCount: number }>(`/ingredient-families/${id}/merge`, { targetFamilyId });
  }

  // Measure Units (for dropdowns)
  async getMeasureUnits(): Promise<{ measureUnits: MeasureUnit[] }> {
    return apiService.get<{ measureUnits: MeasureUnit[] }>('/measure-units');
  }

  // Export functionality
  async exportIngredients(params?: { search?: string; ingredientFamilyId?: string; name?: string; status?: string; ids?: string[] }): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (params?.search) queryParams.append('search', params.search);
    if (params?.ingredientFamilyId) queryParams.append('ingredientFamilyId', params.ingredientFamilyId);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));

    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/ingredients/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  async exportIngredientFamilies(params?: { search?: string; status?: 'ACTIVE' | 'INACTIVE'; ids?: string[] }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/ingredient-families/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
}

export const ingredientService = new IngredientService();
